import type { CaptureResult } from 'posthog-js';

import { isBenignMapsTeardownError } from '@/lib/maps/maps-teardown-errors';

const SENSITIVE_PROP_KEY =
  /^(.*_)?(email|password|token|secret|authorization|api[_-]?key|dsn|cookie|session)(_.*)?$/i;

/**
 * Keys posthog-js itself writes onto every event. `token` is the public
 * project key and is required for ingestion: posthog-js drops any event whose
 * `before_send` removed it. Scrubbing it (it matches `token` above) silently
 * dropped every event and replay from #250 until this fix. `$`-prefixed keys
 * (`$session_id`, `$device_id`, ...) are SDK-owned too, never app payloads.
 */
function isPostHogReservedKey(key: string): boolean {
  return key === 'token' || key === 'distinct_id' || key.startsWith('$');
}

function scrubProperties(
  props: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
  if (!props) return props;
  let changed = false;
  const next: Record<string, unknown> = { ...props };
  for (const key of Object.keys(next)) {
    if (!isPostHogReservedKey(key) && SENSITIVE_PROP_KEY.test(key)) {
      delete next[key];
      changed = true;
    }
  }
  return changed ? next : props;
}

function exceptionFromCapture(event: CaptureResult): unknown | undefined {
  const props = event.properties;
  if (!props || typeof props !== 'object') return undefined;
  const message =
    typeof props.$exception_message === 'string'
      ? props.$exception_message
      : typeof props.$exception_type === 'string'
        ? props.$exception_type
        : undefined;
  if (!message) return undefined;
  return new Error(message);
}

/**
 * PostHog `before_send` hook: drop known Maps teardown noise and never forward
 * properties whose keys look like credentials or PII containers.
 */
export function posthogBeforeSend(event: CaptureResult | null): CaptureResult | null {
  if (!event) return null;

  const originalException = exceptionFromCapture(event);
  if (originalException !== undefined && isBenignMapsTeardownError(originalException)) {
    return null;
  }

  const scrubbed = scrubProperties(event.properties as Record<string, unknown> | undefined);
  if (scrubbed !== event.properties) {
    return { ...event, properties: scrubbed ?? {} };
  }

  return event;
}
